import { BrandLogo } from "../../components/Chrome";
import { useState, type ReactNode } from "react";
import { Lightbulb, LockKeyhole } from "lucide-react";
import { Button } from "../../components/ui";

export function PunchShell({ children, testId, eyebrow = "GamedayTime" }: { children: ReactNode; testId: string; eyebrow?: string }) {
  return (
    <main data-testid={testId} className="min-h-screen px-4 py-6 text-zinc-100 sm:py-10">
      <div className="glass-raised mx-auto max-w-md animate-rise space-y-6 rounded-[26px] p-5 sm:p-7">
        <header>
          <div className="mb-5 flex justify-center"><BrandLogo size="sm" /></div>
          <p className="label-caps text-signal">{eyebrow}</p>
          <h1 className="mt-1 font-display text-3xl font-light text-white">GamedayTime</h1>
        </header>
        {children}
      </div>
    </main>
  );
}

export function BlockedHelp() {
  return (
    <div data-testid="punch-blocked-help" className="mt-4 space-y-2 rounded-sm border border-white/10 bg-ink p-4 text-xs text-zinc-300">
      <p className="flex items-center gap-2 font-semibold text-white"><LockKeyhole className="h-4 w-4" /> Turn location back on</p>
      <p><strong className="text-white">iPhone (Safari):</strong> Settings → Privacy &amp; Security → Location Services → On, then Safari Websites → "While Using". Or tap "aA" in the address bar → Website Settings → Location → Allow.</p>
      <p><strong className="text-white">Android (Chrome):</strong> tap the lock icon by the address → Permissions → Location → Allow. Also check Settings → Location is on.</p>
      <p>Then text {`IN`} or {`OUT`} again for a new link, or request assistance below.</p>
    </div>
  );
}

export function AccuracyTips() {
  return (
    <details data-testid="punch-accuracy-tips" className="rounded-sm border border-white/10 p-4 text-xs text-zinc-400">
      <summary className="flex cursor-pointer items-center gap-2 font-semibold text-zinc-200"><Lightbulb className="h-4 w-4 text-warn" /> Tips for stadiums &amp; indoors</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>Turn on Wi-Fi (even without joining a network). It improves accuracy.</li>
        <li>Step near an opening, concourse window or outside the gate for a few seconds.</li>
        <li>Make sure "Precise Location" is on for your browser.</li>
        <li>If location is blocked, see the help shown after you try. iPhone: Settings → Privacy → Location Services. Android: Chrome lock icon → Permissions.</li>
      </ul>
    </details>
  );
}

export function AssistanceForm({ done, onSubmit }: { done: boolean; onSubmit: (note: string) => Promise<void> }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (done) return <p data-testid="punch-assistance-done" className="rounded-sm border border-emerald-400/30 bg-emerald-400/5 p-4 text-sm text-zinc-200">Assistance requested. An admin will review this punch. Your text time is saved.</p>;
  return (
    <form data-testid="punch-assistance-form" className="space-y-3 rounded-sm border border-white/15 p-4"
      onSubmit={async (e) => { e.preventDefault(); setBusy(true); await onSubmit(note); setBusy(false); }}>
      <label htmlFor="assist-note" className="label-caps text-zinc-400">Request Assistance / Correction</label>
      <textarea id="assist-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} placeholder="Optional: what happened?"
        className="w-full rounded-sm border border-white/15 bg-surface p-3 text-sm text-white" data-testid="punch-assistance-note" />
      <Button type="submit" className="w-full" loading={busy} data-testid="punch-assistance-submit">Send to admin</Button>
    </form>
  );
}
